import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const THEME_KEY = "theme";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getSystemTheme(): Theme {
  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

export default function ThemeWrapper({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(THEME_KEY) as Theme | null;
      if (stored === "dark" || stored === "light" || stored === "system")
        return stored;
      return "system";
    }
    return "system";
  });

  // Listen for system theme changes if theme is 'system'
  useEffect(() => {
    if (theme !== "system") return;
    const handler = () => {
      const sysTheme = getSystemTheme();
      applyTheme(sysTheme);
    };
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", handler);
    handler();
    return () => {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", handler);
    };
  }, [theme]);

  // Apply theme to body and html
  function applyTheme(t: Theme) {
    const resolved = t === "system" ? getSystemTheme() : t;
    if (typeof document !== "undefined") {
      if (resolved === "dark") {
        document.body.classList.add("dark");
        document.documentElement.setAttribute("data-ag-theme-mode", "dark");
      } else {
        document.body.classList.remove("dark");
        document.documentElement.setAttribute("data-ag-theme-mode", "light");
      }
    }
  }

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === "dark" ? "light" : "dark")),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeWrapper");
  }
  return context;
};
